import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Container,
  Box,
  IconButton,
  CardMedia,
  CircularProgress,
  Alert,
  Avatar,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, Check, Close, Image as ImageIcon } from '@mui/icons-material';
import { CustomerRequirement } from 'types/service';
import {
  getCustomerRequirements,
  deleteCustomerRequirement,
  updateCustomerRequirement
} from 'services/customerRequirementService';

const CustomerRequirementsList: React.FC = () => {
  const [requirements, setRequirements] = useState<CustomerRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        const data = await getCustomerRequirements();
        setRequirements(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch customer requirements');
        setLoading(false);
      }
    };

    fetchRequirements();
  }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this requirement?')) {
      try {
        await deleteCustomerRequirement(id);
        setRequirements(requirements.filter(item => item.requirement_id !== id));
      } catch (err) {
        setError('Failed to delete requirement');
      }
    }
  };

  const toggleRequirementStatus = async (id: number, currentStatus: boolean) => {
    try {
      const formData = new FormData();
      formData.append('status', String(!currentStatus));

      const updatedRequirement = await updateCustomerRequirement(id, formData);

      setRequirements(prev =>
        prev.map(req =>
          req.requirement_id === id ? { ...req, status: updatedRequirement.status } : req
        )
      );
    } catch (err) {
      setError('Failed to update requirement status');
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ my: 2 }}>
      {error}
    </Alert>
  );

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
       
        <Typography variant="h4"  fontWeight="bold"> Customer Requiremen </Typography>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          component={Link}
          to="/customer-requirements/new"
        >
          Add New Requirement
        </Button>
      </Box>

      <Grid container spacing={3}>
        {requirements.map((item) => (
          <Grid item key={item.requirement_id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" gap={2}>
                  {/*<Avatar
                    src={item.icon_url || item.image_url}
                    alt={item.name}
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'grey.300'
                    }}
                  >
                    <ImageIcon />
                  </Avatar> */}
                  <Box sx={{ flex: 1, position: 'relative', minHeight: 80 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6">{item.name}</Typography>
                      <Box>
                        <IconButton
                          size="small"
                          color="primary"
                          component={Link}
                          to={`/customer-requirements/edit/${item.requirement_id}`}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(item.requirement_id)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box display="flex" gap={1} mb={1}>
                      <Chip
                        size="small"
                        label={item.status ? 'Active' : 'Inactive'}
                        color={item.status ? 'success' : 'default'}
                        variant={item.status ? 'filled' : 'outlined'}
                        icon={item.status ? <Check fontSize="small" /> : <Close fontSize="small" />}
                        onClick={() => toggleRequirementStatus(item.requirement_id, item.status)}
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip
                        size="small"
                        label={item.is_mandatory ? 'Mandatory' : 'Optional'}
                        color={item.is_mandatory ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        mb: 1
                      }}
                    >
                      {item.description || 'No description provided'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default CustomerRequirementsList;
